import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Upload, ShieldCheck, Copy, Info } from "lucide-react";
import { api, brl } from "@/lib/api";
import { PlatformBadge } from "@/components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PLATS = ["ifood", "99food", "keeta"];
const STEPS = [
  { icon: Upload, t: "Anexe a reclamação", d: "Print da avaliação, do cancelamento ou do pedido de reembolso." },
  { icon: ShieldCheck, t: "Adicione suas provas", d: "Foto do pedido antes da coleta, lacre e comanda." },
  { icon: Copy, t: "Copie e envie", d: "Motivo, submotivo e texto prontos, já no limite de caracteres da plataforma." },
];
const FAQ = [
  ["Funciona para qual plataforma?", "iFood, 99Food e Keeta. Cada plataforma tem a própria tabela de motivos e submotivos e o limite de caracteres certo para cancelamentos, reembolsos e avaliações."],
  ["A defesa garante a aprovação?", "Não. A força estimada mede a qualidade das provas e do argumento, mas a decisão é sempre da plataforma."],
  ["Os textos inventam informações?", "Nunca. A defesa usa somente o que está no caso e nas imagens. Quando falta prova, você é avisado do que anexar."],
  ["Meus dados ficam seguros?", "Sim. Cada restaurante acessa apenas os próprios dados, as imagens ficam em armazenamento privado e seguimos as boas práticas da LGPD."],
  ["Tem fidelidade ou renovação automática?", "Não. O pagamento é avulso via Pix pelo Mercado Pago: cada pagamento de R$ 59,90 libera 30 dias do plano Profissional. Avisamos alguns dias antes do vencimento."],
];

function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch((e) => console.warn("plans load failed", e)); }, []);
  return (
    <div className="mx-auto grid max-w-3xl gap-5 md:grid-cols-2">
      {plans.map((p) => (
        <div key={p.plan_id} data-testid={`landing-plan-${p.plan_id}`} className={`flex flex-col rounded-2xl border bg-white p-7 ${p.price > 0 ? "border-amber-400" : "border-slate-200"}`}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">{p.name}</p>
          <p className="mt-3 font-display text-4xl font-extrabold text-slate-900">{p.price > 0 ? brl(p.price) : "Grátis"}<span className="text-base font-medium text-slate-500">{p.price > 0 ? " / 30 dias" : ""}</span></p>
          <ul className="mt-6 flex-1 space-y-2.5">{p.features.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-600"><Check className="h-4 w-4 shrink-0 text-emerald-600" />{f}</li>)}</ul>
          <Link to="/cadastro" data-testid={`landing-plan-cta-${p.plan_id}`} className={`mt-7 inline-flex h-12 items-center justify-center gap-2 rounded-xl font-semibold ${p.price > 0 ? "ai-cta text-white" : "border border-slate-300 text-slate-900 hover:bg-slate-50"}`}>
            {p.price > 0 ? "Começar e assinar" : "Criar conta grátis"} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      ))}
    </div>
  );
}

const Section = ({ id, title, children }) => (
  <section id={id} className="px-5 py-16 md:px-10">
    <div className="mx-auto max-w-5xl">
      <h2 className="text-center font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">{title}</h2>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5 md:px-10">
          <img src="/logo-full.png" alt="Contestafood" className="h-8 w-auto sm:h-9" />
          <nav className="flex items-center gap-3 sm:gap-5">
            <a href="#precos" className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 sm:block">Preços</a>
            <a href="#duvidas" className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 sm:block">Dúvidas</a>
            <Link to="/login" data-testid="landing-login-btn" className="text-sm font-semibold text-slate-900 hover:text-amber-600">Entrar</Link>
            <Link to="/cadastro" data-testid="landing-header-signup-btn" className="ai-cta inline-flex h-10 items-center rounded-xl px-4 text-sm font-bold text-white">Criar conta</Link>
          </nav>
        </div>
      </header>

      <section className="px-5 pb-12 pt-16 text-center md:px-10 md:pt-24">
        <div className="fade-up mx-auto max-w-3xl">
          <div className="flex flex-wrap items-center justify-center gap-2" data-testid="landing-platforms">
            <span className="text-xs font-semibold text-slate-500">Para restaurantes no</span>{PLATS.map((p) => <PlatformBadge key={p} platform={p} />)}
          </div>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] text-slate-900 sm:text-5xl lg:text-6xl">Conteste reclamações injustas <span className="text-amber-500">em minutos.</span></h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-600 md:text-lg">Anexe a reclamação e as provas do pedido. Você recebe o motivo, o submotivo e o texto pronto para contestar cancelamentos, reembolsos e avaliações negativas.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/cadastro" data-testid="landing-hero-signup-btn" className="ai-cta inline-flex h-14 items-center justify-center gap-2 rounded-2xl px-7 text-base font-bold text-white">Começar grátis: 10 contestações <ArrowUpRight className="h-5 w-5" /></Link>
            <a href="#precos" data-testid="landing-see-pricing" className="inline-flex h-14 items-center justify-center rounded-2xl border border-slate-300 bg-white px-7 font-semibold text-slate-900 hover:bg-slate-100">Ver preço</a>
          </div>
          <p className="mt-4 text-xs text-slate-500">Teste grátis, sem pagamento antecipado</p>
        </div>
      </section>

      <Section title="Como funciona">
        <div className="grid gap-5 md:grid-cols-3" data-testid="landing-steps">
          {STEPS.map((s, i) => (
            <div key={s.t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50"><s.icon className="h-5 w-5 text-amber-600" /></span>
              <p className="mt-4 text-xs font-bold text-slate-400">PASSO {i + 1}</p>
              <p className="font-display text-lg font-bold text-slate-900">{s.t}</p>
              <p className="mt-1 text-sm text-slate-500">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <div id="precos" className="bg-white"><Section title="Preço"><Pricing /></Section></div>

      <div id="duvidas"><Section title="Perguntas frequentes">
        <Accordion type="single" collapsible className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white px-6" data-testid="landing-faq">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}>
              <AccordionTrigger className="text-left font-display font-bold text-slate-900">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section></div>

      <footer className="border-t border-slate-200 bg-white px-5 py-8 md:px-10">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 text-sm text-slate-500 sm:flex-row">
          <img src="/logo-full.png" alt="Contestafood" className="h-7 w-auto" />
          <p className="flex items-center gap-1.5 text-xs"><Info className="h-3.5 w-3.5" /> A força estimada não representa garantia de aprovação da plataforma.</p>
          <Link to="/privacidade" data-testid="landing-privacy-link" className="hover:text-slate-900">Política de Privacidade</Link>
        </div>
      </footer>
    </div>
  );
}
